/**
 Copyright Highway9 Networks Inc.
 */
import { CarrierGatewayCore } from "~/types/CarrierGatewayCore";
import { IPSecParams, OperatorQuery, OperatorRadioQuery, bandConfigData } from "../types/OperatorQuery";
import APIService, { MetricsQuery } from "./APIServices";
import { PaginationData } from "~/types/dataObject";
import { EdgeConnectedDevice } from "~/types/EdgeConnectedDevice";
import { EdgeConnectedRadio } from "~/types/EdgeConnectedRadio";

// Create APIService instances for different endpoints
const operatorAPI = new APIService<OperatorQuery>("operator");
const operatorsQueryAPI = new APIService<OperatorQuery>("operators");
const operatorRadiosAPI = new APIService<OperatorRadioQuery>("operatorRadios");
const operatorRadioAPI = new APIService<OperatorRadioQuery | bandConfigData>("operatorRadio");
const carrierGatewayCoresAPI = new APIService<CarrierGatewayCore>("carriergatewaycores");
const carrierGatewayDevicesAPI = new APIService<EdgeConnectedDevice>("carriergatewaydevices");
const carrierGatewayRadiosAPI = new APIService<EdgeConnectedRadio>("carriergatewayradios");
const carrierInfoAPI = new APIService<CarrierInfo>("carrierInfo");

async function getAllOperators() {
  try {
    return await operatorAPI.getAll() as OperatorQuery[];
  } catch (error) {
    console.log(error);
    throw error;
  }
}

async function getOperatorsByEdge(edgeId: string): Promise<OperatorQuery[]> {
  try {
    const payload = { edgeId };
    return await operatorsQueryAPI.post<{ edgeId: string }>("query", payload) as unknown as OperatorQuery[];
  } catch (error) {
    console.log(error);
    throw error;
  }
}

interface OperatorRadioPayload {
  operatorIds: string[];
}
async function getOperatorRadios(payload: OperatorRadioPayload ) {
  try {
    return await operatorRadiosAPI.post<OperatorRadioPayload>("query", payload) as unknown as OperatorRadioQuery[];
  } catch (error) {
    console.log(error);
    throw error;
  }
}

export type EdgePayload = {
  edgeId: string;
  pageNum?: number;
  pageSize?: number;
}

async function getConnectedEdgeDevices(payload: EdgePayload) {
  try {
    return await carrierGatewayDevicesAPI.postQuery<EdgePayload>("query", payload) as {
      data: EdgeConnectedDevice[];
      paginationData?: PaginationData;
    };
  } catch (error) {
    console.log(error);
    throw error;
  }
}

async function getEdgeConnectedRadios(payload: EdgePayload) {
  try {
    return await carrierGatewayRadiosAPI.postQuery<EdgePayload>("query", payload) as {
      data: EdgeConnectedRadio[];
      paginationData?: PaginationData;
    };
  } catch (error) {
    console.log(error);
    throw error;
  }
}

async function createOperatorRadio(payload: OperatorRadioQuery | bandConfigData) {
  try {
    return await operatorRadioAPI.create(payload);
  } catch (error) {
    console.log(error);
    throw error;
  }
}

async function updateOperatorRadio(payload: OperatorRadioQuery | bandConfigData, id: string) {
  try {
    return await operatorRadioAPI.update(payload, id);
  } catch (error) {
    console.log(error);
    throw error;
  }
}

async function deleteOperatorRadio(id: string | undefined) {
  try {
    if (!id) {
      throw new Error("ID is required for deletion");
    }
    return await operatorRadioAPI.delete(id);
  } catch (error) {
    console.log(error);
    throw error;
  }
}

async function deleteOperator(id: string) {
  console.log("Deleting operator : " + id);
  return operatorAPI.delete(id);
}

async function createOperator(data: OperatorQuery) {
  return await operatorAPI.create(data);
}

async function updateOperator(data: Partial<OperatorQuery>, id: string) {
  return await operatorAPI.update(data, id);
}

async function getCarrierMetrics(id: string) {
  const payload = {
    ids: [id],
    metrics: ["h9_ueid_ul_bytes", "h9_ueid_dl_bytes"],
    resolution: 86400,
  } as MetricsQuery;
  const result = await operatorAPI.getMetrics(payload);

  const res: { [key: string]: number } = {};

  result.forEach((metric) => {
    const dataPoints = metric.metricData[0].dataPoints;
    if (!dataPoints) return;
    const latestValue = dataPoints[dataPoints.length - 1]?.[1] ?? 0;
    res[metric.metric] = latestValue;
  });
  return res;
}

async function getIPSecParams(){
  return await operatorAPI.get("ipsecParams") as unknown as IPSecParams[];
}

interface CorePayload {
  operatorId: string;
  pageNum?: number;
  pageSize?: number;
}
async function getCarrierCore(requestBody: CorePayload) {
  const response = await carrierGatewayCoresAPI.postQuery<CorePayload>("query", requestBody)
  return {
    data: response.data as CarrierGatewayCore[],
    paginationData: response.paginationData ? response.paginationData as PaginationData : undefined
  }
}


export interface CarrierInfo {
  id: string;
  type: string;
  creationTime: number;
  updatedTime: number;
  name: string;
  shortName?: string;
  displayColor?: string;
  plmnList: {
    plmn: string;
    description?: string;
  }[];
}

async function getCarrierInfo() {
  try {
    return await carrierInfoAPI.getAll() as CarrierInfo[];
  } catch (error) {
    console.log(error);
    throw error;
  }
}

export const operatorService = {
  getAllOperators,
  getCarrierInfo,
  getOperatorsByEdge,
  deleteOperator,
  createOperator,
  updateOperator,

  getCarrierMetrics,
  getCarrierCore,

  getOperatorRadios,
  createOperatorRadio,
  updateOperatorRadio,
  deleteOperatorRadio,

  getConnectedEdgeDevices,
  getEdgeConnectedRadios,
  getIPSecParams,
};
